import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

/**
 * English and Arabic, with full RTL.
 *
 * The direction is set on <html> so the whole layout mirrors — Tailwind's
 * logical properties (ms-/me-/start/end) then do the right thing without a
 * parallel set of RTL styles.
 */
const en = {
  common: {
    appName: 'iBMS',
    save: 'Save', cancel: 'Cancel', delete: 'Delete', edit: 'Edit', close: 'Close',
    create: 'Create', search: 'Search', loading: 'Loading…', retry: 'Retry',
    confirm: 'Confirm', copy: 'Copy', copied: 'Copied', download: 'Download',
    none: 'None', never: 'Never', all: 'All', yes: 'Yes', no: 'No',
    signOut: 'Sign out', settings: 'Settings', back: 'Back', next: 'Next', done: 'Done',
    live: 'Live', reconnecting: 'Reconnecting…', offline: 'Offline',
  },
  nav: {
    overview: 'Overview', dashboards: 'Dashboards', devices: 'Devices',
    discovery: 'Discovery', gateways: 'Gateways', alarms: 'Alarms',
    analytics: 'Analytics', reports: 'Reports', locations: 'Locations',
    controls: 'Controls', workOrders: 'Work orders',
    settings: 'Settings', users: 'Users', apiKeys: 'API keys', profiles: 'Device profiles',
  },
  auth: {
    signIn: 'Sign in', email: 'Email', password: 'Password',
    totp: 'Authentication code', totpHint: 'Enter the 6-digit code from your authenticator app',
    setupTitle: 'Set up iBMS', setupIntro: 'Create the first organisation and its owner account.',
    organisation: 'Organisation name', yourName: 'Your name',
    forgot: 'Forgot your password?', signingIn: 'Signing in…',
  },
  device: {
    discovered: 'Discovered', active: 'Active', maintenance: 'Maintenance',
    decommissioned: 'Decommissioned', ignored: 'Ignored',
    battery: 'Battery', signal: 'Signal', lastSeen: 'Last seen', adopt: 'Adopt',
    rename: 'Rename', bulkRename: 'Bulk rename',
  },
  alarm: { critical: 'Critical', warning: 'Warning', info: 'Info',
           acknowledge: 'Acknowledge', acknowledged: 'Acknowledged', cleared: 'Cleared' },
};

const ar: typeof en = {
  common: {
    appName: 'iBMS',
    save: 'حفظ', cancel: 'إلغاء', delete: 'حذف', edit: 'تعديل', close: 'إغلاق',
    create: 'إنشاء', search: 'بحث', loading: 'جارٍ التحميل…', retry: 'إعادة المحاولة',
    confirm: 'تأكيد', copy: 'نسخ', copied: 'تم النسخ', download: 'تنزيل',
    none: 'لا شيء', never: 'أبداً', all: 'الكل', yes: 'نعم', no: 'لا',
    signOut: 'تسجيل الخروج', settings: 'الإعدادات', back: 'رجوع', next: 'التالي', done: 'تم',
    live: 'مباشر', reconnecting: 'إعادة الاتصال…', offline: 'غير متصل',
  },
  nav: {
    overview: 'نظرة عامة', dashboards: 'لوحات المعلومات', devices: 'الأجهزة',
    discovery: 'الاكتشاف', gateways: 'البوابات', alarms: 'الإنذارات',
    analytics: 'التحليلات', reports: 'التقارير', locations: 'المواقع',
    controls: 'التحكم', workOrders: 'أوامر العمل',
    settings: 'الإعدادات', users: 'المستخدمون', apiKeys: 'مفاتيح API', profiles: 'ملفات الأجهزة',
  },
  auth: {
    signIn: 'تسجيل الدخول', email: 'البريد الإلكتروني', password: 'كلمة المرور',
    totp: 'رمز المصادقة', totpHint: 'أدخل الرمز المكون من ٦ أرقام من تطبيق المصادقة',
    setupTitle: 'إعداد iBMS', setupIntro: 'أنشئ أول منظمة وحساب المالك.',
    organisation: 'اسم المنظمة', yourName: 'اسمك',
    forgot: 'هل نسيت كلمة المرور؟', signingIn: 'جارٍ تسجيل الدخول…',
  },
  device: {
    discovered: 'مكتشف', active: 'نشط', maintenance: 'صيانة',
    decommissioned: 'خارج الخدمة', ignored: 'متجاهل',
    battery: 'البطارية', signal: 'الإشارة', lastSeen: 'آخر ظهور', adopt: 'اعتماد',
    rename: 'إعادة تسمية', bulkRename: 'إعادة تسمية جماعية',
  },
  alarm: { critical: 'حرج', warning: 'تحذير', info: 'معلومة',
           acknowledge: 'إقرار', acknowledged: 'تم الإقرار', cleared: 'تم المسح' },
};

export const LANGUAGES = [
  { code: 'en', label: 'English', dir: 'ltr' as const },
  { code: 'ar', label: 'العربية', dir: 'rtl' as const },
];

const stored = (() => {
  try { return localStorage.getItem('ibms.lang'); } catch { return null; }
})();

void i18n.use(initReactI18next).init({
  resources: { en: en as any, ar: ar as any },
  lng: stored ?? 'en',
  fallbackLng: 'en',
  defaultNS: 'common',
  ns: ['common', 'nav', 'auth', 'device', 'alarm'],
  interpolation: { escapeValue: false },
});

export function setLanguage(code: string) {
  void i18n.changeLanguage(code);
  const dir = LANGUAGES.find((l) => l.code === code)?.dir ?? 'ltr';
  document.documentElement.lang = code;
  document.documentElement.dir = dir;
  try { localStorage.setItem('ibms.lang', code); } catch { /* private mode */ }
}

export const currentDirection = () =>
  (LANGUAGES.find((l) => l.code === i18n.language)?.dir ?? 'ltr');

export default i18n;
